import { useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';

/**
 * AgentStageBanner — prominent banner above chat messages showing active agent.
 *
 * Placed OUTSIDE the scroll container so it's always visible at the top.
 * Uses strong, visible colors to clearly indicate which agent is active.
 */
export default function AgentStageBanner({ stageLabel, cancellable, onCancel, primaryColor }) {
  const prevLabel = useRef('');
  const { isDark } = useTheme();

  const isNew = stageLabel && stageLabel !== prevLabel.current;
  useEffect(() => {
    prevLabel.current = stageLabel || '';
  }, [stageLabel]);

  if (!stageLabel) return null;

  const color = primaryColor || '#3B5BDB';

  // Build stronger, more visible color palette
  const bgColor = isDark ? `${color}25` : `${color}14`;
  const borderColor = isDark ? `${color}60` : `${color}50`;
  const dotBgColor = isDark ? `${color}40` : `${color}30`;
  const badgeBgColor = isDark ? `${color}35` : `${color}20`;
  const labelColor = isDark ? '#E5E7EB' : color;
  const badgeColor = isDark ? '#D1D5DB' : color;
  const subtextColor = isDark ? '#9CA3AF' : '#6B7280';
  const cancelBg = isDark ? '#7f1d1d20' : '#fef2f2';
  const cancelBorder = isDark ? '#ef444460' : '#fca5a5';
  const cancelHoverBg = isDark ? 'hover:bg-red-900/30' : 'hover:bg-red-100';

  return (
    <div
      className="flex-shrink-0 flex items-center justify-between px-5 py-3 mx-4 mt-2 mb-0 rounded-xl shadow-md"
      style={{
        backgroundColor: bgColor,
        border: `2px solid ${borderColor}`,
      }}
    >
      <div className="flex items-center gap-3">
        {/* Animated indicator dot */}
        <div
          className="flex items-center justify-center w-8 h-8 rounded-full"
          style={{ backgroundColor: dotBgColor }}
        >
          <span className="relative flex h-3 w-3">
            {isNew && (
              <span
                className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                style={{ backgroundColor: color }}
              />
            )}
            <span
              className={`relative inline-flex rounded-full h-3 w-3 ${!isNew ? 'animate-pulse' : ''}`}
              style={{ backgroundColor: color }}
            />
          </span>
        </div>

        {/* Label and status */}
        <div>
          <div className="flex items-center gap-2.5">
            <span className="font-bold text-sm tracking-tight" style={{ color: labelColor }}>
              {stageLabel}
            </span>
            <span
              className="text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider"
              style={{ backgroundColor: badgeBgColor, color: badgeColor }}
            >
              Active
            </span>
          </div>
          <span className="text-xs mt-0.5 block" style={{ color: subtextColor }}>
            {cancellable
              ? "Type 'cancel' or click Cancel to exit this flow"
              : 'Processing — please wait for completion'}
          </span>
        </div>
      </div>

      {/* Cancel button */}
      {cancellable && onCancel && (
        <button
          onClick={onCancel}
          className={`text-xs px-4 py-1.5 rounded-lg border font-semibold transition-all ${cancelHoverBg}`}
          style={{
            borderColor: cancelBorder,
            color: '#ef4444',
            backgroundColor: cancelBg,
          }}
        >
          Cancel
        </button>
      )}
    </div>
  );
}
